<template>
  <b-container v-observe-visibility="visibilityChanged">
    <h4>{{ title }}<span v-if="allowMultipleUploads">s</span></h4>
    <slot name="additional-info" />
    <b-alert
      v-if="selectedFile"
      :show="selectedFile.error.length > 0"
      variant="danger">
      {{ selectedFile.error }}
    </b-alert>
    <div
      v-if="constraint"
      style="line-height: 1.4">
      File must be one of the following extensions: {{ constraint.extensions }}.
      <span v-if="constraint.enforceStrictImageDimensions"> It must have dimensions
        that are {{ constraint.imageWidth }}x{{ constraint.imageHeight }}px.</span>
    </div>
    <div
      id="imageupload"
      class="text-center p-5">
      <h5>Drop file<span v-if="allowMultipleUploads">(s)</span> anywhere to upload<br>or</h5>
      <label
        :for="inputId"
        class="btn xl-wide btn-primary">Select File<span v-if="allowMultipleUploads">(s)</span></label>
      <file-upload
        ref="upload"
        v-model="files"
        :input-id="inputId"
        :headers="authHeader"
        :drop="true"
        :multiple="allowMultipleUploads"
        :post-action="dynamicPostAction"
        :name="inputName"
        :type="accept"
        :accept="constraint ? constraint.accept : accept"
        :extensions="extensions"
        @input-filter="inputFilter" />
    </div>
    <div
      class="table-responsive"
      style="max-height: 400px">
      <table class="table table-hover">
        <thead>
          <tr>
            <th>Thumb</th>
            <th>Name</th>
            <th class="text-right">
              Size
            </th>
            <th class="text-right">
              Aspect
            </th>
            <th class="text-center">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="file in files"
            :key="file.id">
            <td>
              <b-img
                v-if="file.thumb"
                :src="file.thumb"
                width="40"
                height="auto" />
              <i
                v-else-if="file.type.includes('pdf')"
                class="fas fa-file-pdf" />
              <span v-else>No Image</span>
            </td>
            <td>
              <div class="filename">
                {{ file.name }}
              </div>
              <div
                v-if="file.active || file.progress !== '0.00'"
                class="progress">
                <div
                  :class="{'progress-bar': true, 'progress-bar-striped': true, 'bg-danger': file.error, 'progress-bar-animated': file.active}"
                  :style="{width: file.progress + '%'}"
                  role="progressbar">
                  {{ file.progress }}%
                </div>
              </div>
            </td>
            <td class="text-right text-monospace">
              <span :class="file.sizeValid ? 'text-success' : 'text-danger'">{{ file.size | formatSize }}</span>
            </td>
            <td
              class="text-right">
              <span
                v-if="hasDimensions(file)"
                :class="file.aspectValid ? 'text-success' : 'text-danger'">{{ file.width }}x{{ file.height }}px</span>
            </td>
            <td class="text-center">
              <b-btn
                size="sm"
                variant="link"
                @click.prevent="$refs.upload.remove(file)">
                Remove
              </b-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div>
      <b-row align-h="between">
        <b-col cols="auto">
          <b-dropdown
            v-if="!cloudinary || hideAdditionalOptions"
            id="options-dropdown"
            text="Additional Options"
            variant="secondary"
            style="padding-right: 180px">
            <b-dropdown-form>
              <b-form-checkbox v-model="formProcessImageCuts">
                Process 3x, 2x, 1x Image Cuts
              </b-form-checkbox>
            </b-dropdown-form>
          </b-dropdown>
        </b-col>
        <b-col cols="auto">
          <b-btn
            class="xl-wide mr-2"
            variant="outline-secondary"
            @click.prevent="onCancel">
            Cancel
          </b-btn>
          <span v-if="$refs.upload && !$refs.upload.uploaded">
            <b-btn
              :disabled="uploadDisabled"
              class="ml-2 xl-wide mr-auto"
              variant="primary"
              @click.prevent="onUpload">
              Upload</b-btn>
          </span>
          <span v-else>
            <b-btn
              :disabled="!selectedFile"
              class="ml-2 xl-wide mr-auto"
              variant="primary"
              @click.prevent="onSave">
              Ok</b-btn>
          </span>
        </b-col>
      </b-row>
    </div>
  </b-container>
</template>

<script>
  import FileUpload from 'vue-upload-component'
  import common from '@/services/common'
  import uuid from 'uuid'

  export default {
    name: 'AssetUpload',
    components: {
      FileUpload,
    },
    props: {
      id: {
        type: String,
        default: null,
      },
      allowMultipleUploads: {
        type: Boolean,
        default: false,
      },
      size: {
        type: String,
        default: '',
      },
      postAction: {
        type: String,
        default: '',
        required: true,
      },
      maxImageSize: {
        type: Number,
        default: null,
        required: false,
      },
      existingFiles: {
        type: Array,
        default: null,
      },
      withoutAuthHeader: {
        type: Boolean,
        default: false,
      },
      cloudinary: {
        type: Object,
        default: null,
      },
      title: {
        type: String,
        default: 'Upload Asset',
      },
      processImageCuts: {
        type: Boolean,
        default: false,
      },
      hideAdditionalOptions: {
        type: Boolean,
        default: false,
      },
      accept: {
        type: String,
        default: undefined,
      },
      extensions: {
        type: String,
        default: undefined,
      },
      constraint: {
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        inputId: uuid.v4(), // Randomly generate the input ID used for the upload component so that multiple instances don't stomp on each other.
        files: [],
        selectedFile: null,
        authHeader: null,
        formProcessImageCuts: this.processImageCuts,
      }
    },
    computed: {
      uploadDisabled() {
        if (!this.files.length || this.$refs.upload.active) {
          return true
        }

        return !this.files.every(f => f.sizeValid && f.aspectValid)
      },
      dynamicPostAction() {
        return `${this.postAction}?processImageCuts=${this.formProcessImageCuts ? 'true' : 'false'}`
      },
      inputName() {
        return this.cloudinary ? 'file' : 'upload'
      },
    },
    watch: {
      files(newValue) {
        if (newValue.length > 0) {
          this.selectedFile = newValue[0]
        } else {
          this.selectedFile = null
        }
      },
      formProcessImageCuts() {
        this.files = [] // due to a bug in form-upload component, the files must be reset when the form checkbox is updated
      },
    },
    async created() {
      if (!this.withoutAuthHeader) {
        this.authHeader = await common.authHeader()
      }
    },
    methods: {
      onSave() {
        if (this.selectedFile) {
          this.$refs.upload.clear()
          this.$emit('asset-url-selected', this.selectedFile.response.url)
          this.$emit('asset-selected-response', this.selectedFile.response)
        }
      },
      onCancel() {
        this.$refs.upload.clear()
        this.$emit('cancel')
      },
      async getCloudinaryUploadData(options = {}) {
        const { upload_preset = 'OMS' } = options

        const params = {
            timestamp: Date.now(),
            upload_preset,
        }

        const paramsStringified = Object
          .entries(params)
          .map(([k, v]) => `${k}=${[v].join(',')}`)
          .sort()
          .join('&')
        const msgUint8 = new TextEncoder().encode(`${paramsStringified}${process.env.VUE_APP_CLOUDINARY_API_SECRET}`)
        const hashBuffer = await crypto.subtle.digest('SHA-1', msgUint8)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        const signature = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')

        return {
          ...params,
          api_key: process.env.VUE_APP_CLOUDINARY_API_KEY,
          signature,
        }
      },
      async onUpload() {
        // Check if any files would be overwritten by this upload
        const filesToOverwrite = this.files.filter(file => {
          if (this.existingFiles && this.existingFiles.length) {
            return this.existingFiles.includes(file.name)
          }
          return false
        })

        // No conflicting file names found, upload away
        if (filesToOverwrite.length === 0) {
          this.$refs.upload.active = true
          return
        }

        let msg
        if (filesToOverwrite.length > 1) {
          msg = `Are you sure you wish to overwrite the following ${filesToOverwrite.length} assets?\n`
          filesToOverwrite.forEach(item => {
            msg += `\n- ${item.name}`
          })
        } else {
          msg = `Are you sure you wish to overwrite ${filesToOverwrite[0].name}?`
        }

        const modalConfirmed = await this.$confirm.open(msg)

        if (modalConfirmed) {
          this.$refs.upload.active = true
        }
      },
      async inputFilter(newFile, oldFile, prevent) {
        if (newFile) {
          if (!newFile.name) {
            return prevent()
          }

          newFile.blob = ''
          let URL = window.URL || window.webkitURL
          if (URL && URL.createObjectURL) {
            newFile.blob = URL.createObjectURL(newFile.file)
          }

          newFile.thumb = ''
          if (newFile.blob && newFile.type.substr(0, 6) === 'image/') {
            newFile.thumb = newFile.blob
          }

          const constraint = this.constraint ? this.constraint : {}
          newFile.width = ''
          newFile.height = ''
          newFile.aspectValid = !constraint.enforceStrictImageDimensions

          //size is valid if maxImageSize is null (indicates no limit)
          newFile.sizeValid = !this.maxImageSize || (newFile.size <= this.maxImageSize)

          if (this.cloudinary) {
            newFile.data = await this.getCloudinaryUploadData({ ...this.cloudinary })
          }

          const image = new Image()
          image.src = newFile.blob
          image.onload = () => {
            newFile.width = image.naturalWidth
            newFile.height = image.naturalHeight
            newFile.aspectValid = !constraint.enforceStrictImageDimensions ||
                (newFile.width === constraint.imageWidth &&
                    newFile.height === constraint.imageHeight)
          }
        }
      },
      visibilityChanged(isVisible) {
        if (!isVisible) {
          this.$refs.upload.clear()
        }
      },
      hasDimensions(file) {
        return file.width !== '' && file.height !== ''
      },
    },
  }
</script>

<style scoped>
  #imageupload:hover, #imageupload:active {
    background-color: rgba(0, 0, 0, 0.075);
  }

</style>
